// Logic
import getCacheKey from "@/vue/planning/logic/getCacheKey"
import { getUniqueId } from "@/vue/planning/logic/uniqueId"

// State
import responsesCache from "@/vue/planning/state/responsesCache"
import pricesLoadingSet from "@/vue/planning/state/pricesLoadingSet"

// Typees
import {
	IResaPrestation,
	PricesResponse,
	WiypResponse,
	WiypPrestation,
	PrestationRaw,
} from "@/types/responses"
import { ISeAvailabilities, PackageOffer } from "@/types"
import { IStoreState } from "@/vue/planning/store/state/state"

// Various
import * as _ from "lodash"

const getWiypPrice = async (
	packageId: string,
	packCode: string,
	liheId: string,
	prestIds: string,
	startDate: string,
	endDate: string,
	familyComposition: string,
	site: string,
	acceptLanguage: string,
	storeObj: { state: IStoreState; [key: string]: any }
): Promise<void> => {
	validateArguments(packageId, packCode, prestIds, familyComposition)
	const cacheKey = getCacheKey(
		packCode,
		startDate,
		endDate,
		storeObj.state.season,
		storeObj.state.participants
	)

	const cacheResult = attemptToUseCache(cacheKey)
	if (cacheResult) return

	const priceResponse = await fetchPrices(
		packageId,
		packCode,
		liheId,
		prestIds,
		startDate,
		endDate,
		familyComposition,
		site,
		acceptLanguage
	)

	updateStoreWithPrices(priceResponse, storeObj)

	updateResponseCache(cacheKey, storeObj)
	
	clearLoadingSet(cacheKey)
}

function validateArguments(
	packageId: string,
	packCode: string,
	prestIds: string,
	familyComposition: string
) {
	if (!packageId) throw new Error("Cannot request prices without packageId")
	if (packCode === "HS") throw new Error("Cannot request prices for HS package")
	if (!prestIds) throw new Error("Cannot request without prestIds")
	if (!familyComposition) throw new Error("Family composition incorrect")
}

function attemptToUseCache (cacheKey: string) {
	return responsesCache.value.getResponse("prices", cacheKey)
}

function updateResponseCache(cacheKey: string, storeObj: { state: IStoreState }): void {
    // Set the response in the cache
    responsesCache.value.setResponse("prices", cacheKey, storeObj.state.prestMaps.prices);
    
    // Clone the cache to ensure immutability
    responsesCache.value = _.cloneDeep(responsesCache.value);
}

function clearLoadingSet(cacheKey: string): void {
    // Delete the cache key from the loading set
    pricesLoadingSet.value.delete(cacheKey);
    
    // Clone the loading set to ensure immutability
    pricesLoadingSet.value = _.cloneDeep(pricesLoadingSet.value);
}

async function fetchPrices(
	packageId: string,
	packCode: string,
	liheId: string,
	prestIds: string,
	startDate: string,
	endDate: string,
	familyComposition: string,
	site: string,
	acceptLanguage: string
): Promise<WiypResponse> {
	try {
		return await window.wiypService.getWiypPrices({
			packageId,
			packCode,
			liheId,
			prestIds,
			startDate,
			endDate,
			familyComposition,
			site,
			acceptLanguage,
		})
	} catch (error) {
		console.error("Failed to fetch prices:", error)
		throw new Error("Failed to fetch prices from service")
	}
}

function updateStoreWithPrices(
	priceResponse: WiypResponse,
	storeObj: { state: IStoreState }
)  {
	const quotePriceMap = _.cloneDeep(storeObj.state.prestMaps.seAvailabilities)
	enrichAvailabilitiesWithPrices(priceResponse, quotePriceMap)
	storeObj.state.calledPrice = true
	storeObj.state.prestMaps.prices = quotePriceMap
}

// Function to enrich availabilities with prices
function enrichAvailabilitiesWithPrices(
    priceResponse: WiypResponse,
    availabilities: ISeAvailabilities
): void {
    const wiypPrestations: WiypPrestation[] = priceResponse.wiypPrices || [];
    const iresaPrestations: IResaPrestation[] = priceResponse.iresaPrices || [];

    Object.keys(availabilities).forEach((packageId: string) => {
        const offers: { [id: string]: PackageOffer } = availabilities[packageId];
        Object.values(offers).forEach((offer: PackageOffer) => {
            const wiypPrestation = wiypPrestations.find(
                (wiyp) => wiyp.prestId === offer.prestIdSource
            );
            if (wiypPrestation) {
                applyWiypPricing(offer, wiypPrestation);
				return
            } else {
				// If no Wiyp price is found, clear any existing pricing on the offer

                clearPricing(offer);
                const iresaPrestation = iresaPrestations.find(
                    (iresa) => iresa.prestId === offer.prestIdSource
                );
                if (iresaPrestation) {
                    applyIresaPricing(offer, iresaPrestation);
                }
            }
        });
    });
}

function applyWiypPricing(offer: PackageOffer, wiypPrestation: WiypPrestation) {
    offer.pricePerLogement = Math.round(wiypPrestation.sellingPriceWiyp);

    if (wiypPrestation.publicPriceWiyp) {
        offer.crossedPrice = wiypPrestation.publicPriceWiyp;
        // Calculate the promo percentage
        const discount = wiypPrestation.publicPriceWiyp - wiypPrestation.sellingPriceWiyp;
        offer.maxPromo = Math.round((discount / wiypPrestation.publicPriceWiyp) * 100);
    } else {
		// If no Wiyp price is found, clear any existing pricing on the offer
        clearPricing(offer);
    }
}

function applyIresaPricing(offer: PackageOffer, iresaPrestation: IResaPrestation) {
    offer.pricePerLogement = Math.round(iresaPrestation.sellingPriceIresa);
}

function clearPricing(offer: PackageOffer) {
    delete offer.crossedPrice;
    delete offer.maxPromo;
}
export default getWiypPrice
